import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LogicBuilder } from './../../models/logic-builder/logic-builder';

import { Subject } from 'rxjs';

import { Action, App, LogicBuilderSet, Project, RecursiveTriggerContainer, SelectableField, Trigger } from '../../models';
import { ActionType } from '../../shared/enums';
import { LogicBuilderDataService } from '../data-services';
import { LogicBuilderLogicServiceModel } from './models';
@Injectable({
    providedIn: 'root'
})
export class LogicBuilderLogicService {
    private _loadedLogicBuildersModel = new Subject<boolean>();
    loadedLogicBuildersModel$ = this._loadedLogicBuildersModel.asObservable();

    model: LogicBuilderLogicServiceModel = new LogicBuilderLogicServiceModel();

    constructor(
        private _logicBuilderDataService: LogicBuilderDataService,
        private _snackBar: MatSnackBar
    ) { }

    getLogicBuildersByProject(app: App, project: Project): void {
        this._logicBuilderDataService.getLogicBuildersByProject(app, project)
            .subscribe({
                next: (logicBuilders: LogicBuilder[]) => {
                    this.updateLogicBuildersInModel(logicBuilders);
                    this.updateSetFieldsAndAndStyleFieldsIsMapPinUsedInModel(logicBuilders);
                },
                error: () => {
                    this._snackBar.open('An error has occurred retrieving the existing Logic Builders', 'Close', { duration: 2000 });
                },
                complete: () => {
                    this._loadedLogicBuildersModel.next(true);
                }
            });
    }

    toggleActiveLogicBuilder(app: App, logicBuilder: LogicBuilder): void {
        this._logicBuilderDataService.toggleActiveLogicBuilder(app, logicBuilder)
            .subscribe({
                next: (logicBuilder: LogicBuilder) => {
                    this.updateSingleLogicBuilderInModel(logicBuilder);
                    this._snackBar.open(`Logic Builder ${logicBuilder.active ? 'activated' : 'deactivated'}`, 'Close', { duration: 2000 });
                },
                error: () => {
                    logicBuilder.active = !logicBuilder.active;
                    this.updateSingleLogicBuilderInModel(logicBuilder);
                    this._snackBar.open(`An error has occurred ${logicBuilder.active ? 'activating' : 'deactivating'} the Logic Builder: ${logicBuilder.name}`, 'Close', { duration: 2000 });
                    this._loadedLogicBuildersModel.next(true);
                },
                complete: () => {
                    this._loadedLogicBuildersModel.next(true);
                }
            });
    }

    deleteLogicBuilder(app: App, logicBuilder: LogicBuilder): void {
        this._logicBuilderDataService.deleteLogicBuilder(app, logicBuilder)
            .subscribe({
                next: () => {
                    this.updateLogicBuildersInModel(this.model.currentProjectLogicBuilders.filter((logicBuilderInSession: LogicBuilder) => logicBuilderInSession.id !== logicBuilder.id));
                    this.updateSetFieldsAndAndStyleFieldsIsMapPinUsedInModel(this.model.currentProjectLogicBuilders);
                    this._snackBar.open(`Logic Builder: ${logicBuilder.name} deleted. `, 'Close', { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(`An error has occurred deleting the Logic Builder: ${logicBuilder.name}`, 'Close', { duration: 2000 });
                },
                complete: () => {
                    this._loadedLogicBuildersModel.next(true);
                }
            });
    }

    updateLogicBuildersInModel(incomingLogicBuilders: LogicBuilder[]): void {
        this.model.currentProjectLogicBuilders = incomingLogicBuilders ?? [];
    }

    updateSingleLogicBuilderInModel(incomingLogicBuilder: LogicBuilder): void {
        this.model.currentProjectLogicBuilders = this.model.currentProjectLogicBuilders.map((logicBuilder: LogicBuilder) => {
            if (logicBuilder.id === incomingLogicBuilder.id) {
                return incomingLogicBuilder;
            }
            return logicBuilder;
        });
    }

    validateFieldsExistForTriggers(logicBuilder: LogicBuilder, triggerContainer: RecursiveTriggerContainer, fields: SelectableField[], invalidLogicBuilderIds: Set<string>): Set<string> {
        if (triggerContainer.trigger) {
            this.validateTrigger(logicBuilder, invalidLogicBuilderIds, triggerContainer.trigger, fields);
        }

        if (triggerContainer.nestedTriggerContainers) {
            this.validateNestedTriggers(logicBuilder, invalidLogicBuilderIds, triggerContainer.nestedTriggerContainers, fields);
        }

        return invalidLogicBuilderIds;
    }

    validateFieldsExistForActions(logicBuilder: LogicBuilder, fields: SelectableField[], invalidLogicBuilderIds: Set<string>): Set<string> {
        if (!logicBuilder.logicBuilderSets[0].actions.some((action: Action) => action.actionType === ActionType.StyleFieldColor || action.actionType === ActionType.SetField)) {
            return invalidLogicBuilderIds;
        }

        const styleFieldOrSetFieldActions = logicBuilder.logicBuilderSets[0].actions.filter((action: Action) => action.actionType === ActionType.StyleFieldColor || action.actionType === ActionType.SetField);
        styleFieldOrSetFieldActions.forEach((action: Action) => {
            if (action.selectedField?.gridColumnId) {
                const fieldDefined = fields.some((field: SelectableField) => field.identifier.fieldId === action.selectedField.fieldId && field.identifier.gridColumnId === action.selectedField.gridColumnId);
                if (!fieldDefined) {
                    invalidLogicBuilderIds.add(logicBuilder.id);
                }
            }
            else {
                const fieldDefined = fields.some((field: SelectableField) => field.identifier.fieldId === action.selectedField?.fieldId);
                if (!fieldDefined) {
                    invalidLogicBuilderIds.add(logicBuilder.id);
                }
            }
        });

        return invalidLogicBuilderIds;
    }

    validateNestedTriggers(logicBuilder: LogicBuilder, invalidLogicBuilderIds: Set<string>, nestedTriggerContainers: RecursiveTriggerContainer[], fields: SelectableField[]): void {
        nestedTriggerContainers.forEach((nestedTriggerContainer: RecursiveTriggerContainer) => {
            if (nestedTriggerContainer.trigger) {
                this.validateTrigger(logicBuilder, invalidLogicBuilderIds, nestedTriggerContainer.trigger, fields);
            }
            if (nestedTriggerContainer.nestedTriggerContainers) {
                this.validateNestedTriggers(logicBuilder, invalidLogicBuilderIds, nestedTriggerContainer.nestedTriggerContainers, fields);
            }
        });
    }

    validateTrigger(logicBuilder: LogicBuilder, invalidLogicBuilderIds: Set<string>, trigger: Trigger, fields: SelectableField[]): string {
        if (trigger.selectedField.gridColumnId) {
            const fieldDefined = fields.some((field: SelectableField) => field.identifier.fieldId === trigger.selectedField.fieldId && field.identifier.gridColumnId === trigger.selectedField.gridColumnId);
            if (!fieldDefined) {
                invalidLogicBuilderIds.add(logicBuilder.id);
            }
            return;
        }

        const fieldDefined = fields.some((field: SelectableField) => field.identifier.fieldId === trigger.selectedField.fieldId);
        if (!fieldDefined) {
            invalidLogicBuilderIds.add(logicBuilder.id);
        }
    }

    updateTriggerFieldsInModel(currentLogicBuilder: LogicBuilder | null): void {
        this.model.triggerFields = [];

        this.model.currentProjectLogicBuilders.forEach((logicBuilder: LogicBuilder) => {
            if (logicBuilder.id === currentLogicBuilder?.id) {
                return;
            }

            logicBuilder.logicBuilderSets.forEach((logicBuilderSet: LogicBuilderSet) => {
                logicBuilderSet.triggers?.forEach((triggerContainer: RecursiveTriggerContainer) => {
                    this.addTriggerFieldsToModel(triggerContainer);
                });
            });
        });
    }

    private updateSetFieldsAndAndStyleFieldsIsMapPinUsedInModel(logicBuilders: LogicBuilder[]): void {
        this.model.setFields = [];
        this.model.styleFields = [];
        this.model.isMapPinUsed = false;

        logicBuilders?.forEach((logicBuilder: LogicBuilder) => {
            logicBuilder?.logicBuilderSets?.forEach((logicBuilderSet: LogicBuilderSet) => {
                logicBuilderSet?.actions?.forEach((action: Action) => {
                    if (action?.actionType === ActionType.SetField) {
                        this.model.setFields.push(action.selectedField);
                    }

                    if (action?.actionType === ActionType.StyleFieldColor) {
                        this.model.styleFields.push(action.selectedField);
                    }

                    if (action?.actionType === ActionType.SetMapPinColor) {
                        this.model.isMapPinUsed = true;
                    }
                });
            });
        });
    }

    private addTriggerFieldsToModel(triggerContainer: RecursiveTriggerContainer): void {
        if (triggerContainer.trigger) {
            this.model.triggerFields.push(triggerContainer.trigger.selectedField);
            return;
        }

        triggerContainer.nestedTriggerContainers?.forEach((nestedTrigger: RecursiveTriggerContainer) => {
            this.addTriggerFieldsToModel(nestedTrigger);
        });
    }
}
